<script setup lang="ts">
import IconMdiCloseCircleOutline from '~icons/mdi/close-circle-outline'
import IconMdiAlertCircle from '~icons/mdi/alert-circle'
import IconMdiRefresh from '~icons/mdi/refresh'
import IconMdiAlert from '~icons/mdi/alert'
import IconMdiClose from '~icons/mdi/close'
import IconMdiGraphOutline from '~icons/mdi/graph-outline'
import IconMdiDatabaseOffOutline from '~icons/mdi/database-off-outline'
import { computed, onMounted, ref, watch } from 'vue'
import TuringSelect from '@/shared/components/forms/TuringSelect.vue'
import Button from '@/shared/components/Button.vue'
import IconButton from '@/shared/components/IconButton.vue'
import { Switch } from '@/components/ui/switch'
import Icon from '@/shared/components/Icon.vue'
import CytoscapeGraph from '@/app/components/features/relations/CytoscapeGraph.vue'
import EntityDetailPanel from '@/app/components/features/relations/EntityDetailPanel.vue'
import GraphLegend from '@/app/components/features/relations/GraphLegend.vue'
import { useCytoscapeRelationGraph } from '@/app/composables/useCytoscapeRelationGraph'
import { useServiceProject } from '@/api/services/project.service'
import { useServiceConcept } from '@/api/services/concept.service'
import { useStoreProject } from '@/stores/project'
import { useStoreApp } from '@/stores/app'
import type { CytoscapeNode, CytoscapeEdge, GraphNode } from '@/app/types/relation-graph.type'
import type { Uuid4 } from '§C/types/system.type'
import { ElementStatus } from '§§M/main/types/enumerated.type'

const storeApp = useStoreApp()
const storeProject = useStoreProject()
const { readProjectForAnalysis, readProjects, readProjectConceptsForAnalysis } = useServiceProject()
const { readConceptForAnalysis } = useServiceConcept()
const { buildGraphFromProject, buildGraphFromConcept } = useCytoscapeRelationGraph()

// State
const selectedProjectUuid = ref<Uuid4 | null>(null)
const selectedProjectConceptUuid = ref<Uuid4 | null>(null)
const includeConcepts = ref(false)
type ViewMode = 'project' | 'concept'
const viewMode = ref<ViewMode>('project')
const graphNodes = ref<CytoscapeNode[]>([])
const graphEdges = ref<CytoscapeEdge[]>([])
const selectedNode = ref<GraphNode | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const nodeCount = ref(0)
const showNodeLimitWarning = ref(false)
const NODE_LIMIT_WARNING_THRESHOLD = 200
const projectConcepts = ref<Array<{ title?: string; uuid: Uuid4 }>>([])  // Store concepts for selected project

// Computed
const projectOptions = computed(() => {
  // Map projects with status information
  const options = storeProject.projectList.map((project: any) => ({
    label: project.title || 'Untitled Project',
    value: project.uuid,
    status: project.status,
    statusLabel: project.status === ElementStatus.PUBLISHED ? 'Published' : 'Draft',
  }))

  // Sort: Published projects first, then by title
  return options.sort((a, b) => {
    // Published projects come first
    if (a.status === ElementStatus.PUBLISHED && b.status !== ElementStatus.PUBLISHED) return -1
    if (a.status !== ElementStatus.PUBLISHED && b.status === ElementStatus.PUBLISHED) return 1
    // Within same status, sort by title
    return a.label.localeCompare(b.label)
  })
})

const currentProjectConceptOptions = computed(() => {
  if (!selectedProjectUuid.value || projectConcepts.value.length === 0) return []

  return projectConcepts.value.map((concept) => ({
    label: concept.title || 'Untitled Concept',
    value: concept.uuid,
  }))
})

const detailPanelOpen = computed(() => selectedNode.value !== null)

// Functions
const loadProjectConcepts = async (projectUuid: Uuid4) => {
  try {
    const { data, status, fetchData } = readProjectConceptsForAnalysis(projectUuid)
    await fetchData()

    if (status.value === 'success' && data.value?.concepts) {
      projectConcepts.value = data.value.concepts
    } else {
      projectConcepts.value = []
    }
  } catch (err) {
    console.error('Error loading project concepts:', err)
    projectConcepts.value = []
  }
}

const loadProjectGraph = async () => {
  if (!selectedProjectUuid.value) return

  loading.value = true
  error.value = null
  showNodeLimitWarning.value = false

  try {
    // If in concept view mode with a concept selected, load concept view instead
    if (viewMode.value === 'concept' && selectedProjectConceptUuid.value) {
      await loadConceptView()
      return
    }

    // Fetch project data - API returns nested structure by default
    const { data: projectData, status: projectStatus, fetchData: fetchProject } = readProjectForAnalysis(selectedProjectUuid.value)

    // Call fetchData to actually fetch the project data
    await fetchProject()

    if (projectStatus.value !== 'success' || !projectData.value) {
      error.value = 'Failed to load project data'
      return
    }

    // Optionally fetch concepts if toggle is enabled
    if (includeConcepts.value) {
      const { data: conceptsData, status: conceptsStatus, fetchData: fetchConcepts } = readProjectConceptsForAnalysis(selectedProjectUuid.value)

      // Fetch concepts
      await fetchConcepts()

      if (conceptsStatus.value === 'success' && conceptsData.value) {
        // Add concepts to the project data (using type assertion since concepts is optional)
        ;(projectData.value.project as any).concepts = conceptsData.value.concepts
      }
    }

    // Transform to Cytoscape graph with the data from API
    // The API returns nested structure including:
    // - facilities
    // - setup_departments with assets and external_identifiers
    // - concepts (if they exist for this project and includeConcepts is true)
    const graph = buildGraphFromProject(projectData.value)

    graphNodes.value = graph.nodes
    graphEdges.value = graph.edges
    selectedNode.value = null

    // Check node count and show warning if needed
    nodeCount.value = graph.nodes.length
    if (nodeCount.value > NODE_LIMIT_WARNING_THRESHOLD) {
      showNodeLimitWarning.value = true
    }
  } catch (err) {
    console.error('Error loading project graph:', err)
    error.value = err instanceof Error ? err.message : 'An unexpected error occurred'
  } finally {
    loading.value = false
  }
}

const retryLoadProject = () => {
  if (selectedProjectUuid.value) {
    loadProjectGraph()
  }
}

const loadConceptView = async () => {
  if (!selectedProjectConceptUuid.value) return

  loading.value = true
  error.value = null
  showNodeLimitWarning.value = false

  try {
    // Fetch concept with all related entities populated
    const populate = [
      'data_lookup_tables',
      'concept_sections',
      'concept_sections.section_departments',
      'concept_sections.section_departments.tank_types',
      'concept_sections.section_departments.object_setups',
      'concept_sections.section_departments.tanks',
      'concept_sections.section_departments.tanks.external_identifiers',
      'concept_sections.section_departments.tanks.incoming_connections',
      'concept_sections.section_departments.tanks.outgoing_connections',
      'concept_sections.section_production_cycles',
      'concept_sections.section_production_cycles.production_cycle_departments',
    ].join(',')

    const { data, status, fetchData } = readConceptForAnalysis(selectedProjectConceptUuid.value, populate)

    await fetchData()

    if (status.value === 'success' && data.value) {
      const graph = buildGraphFromConcept(data.value)

      graphNodes.value = graph.nodes
      graphEdges.value = graph.edges
      selectedNode.value = null

      nodeCount.value = graph.nodes.length
      if (nodeCount.value > NODE_LIMIT_WARNING_THRESHOLD) {
        showNodeLimitWarning.value = true
      }
    } else if (status.value === 'error') {
      error.value = 'Failed to load concept data'
    }
  } catch (err) {
    console.error('Error loading concept view:', err)
    error.value = err instanceof Error ? err.message : 'An unexpected error occurred'
  } finally {
    loading.value = false
  }
}

const clearSelection = () => {
  selectedProjectUuid.value = null
  selectedProjectConceptUuid.value = null
  includeConcepts.value = false
  viewMode.value = 'project'
  graphNodes.value = []
  graphEdges.value = []
  selectedNode.value = null
  error.value = null
  nodeCount.value = 0
  showNodeLimitWarning.value = false
  projectConcepts.value = []
}

const dismissNodeLimitWarning = () => {
  showNodeLimitWarning.value = false
}

const handleNodeClick = (node: CytoscapeNode) => {
  // Convert CytoscapeNode to GraphNode format for the detail panel
  selectedNode.value = {
    id: node.data.id,
    type: 'default',
    position: { x: 0, y: 0 },
    data: {
      entityType: node.data.entityType,
      entityData: node.data.entityData,
      label: node.data.label,
      isCollection: node.data.isCollection,
    },
  } as GraphNode
}

const handleDetailPanelClose = () => {
  selectedNode.value = null
}

// Watchers
watch(selectedProjectUuid, async (newValue) => {
  if (newValue) {
    // Clear concept selections when project is selected
    selectedProjectConceptUuid.value = null
    projectConcepts.value = []

    // Load concepts for the selected project
    await loadProjectConcepts(newValue)

    // Load project graph
    loadProjectGraph()
  }
})

watch(includeConcepts, () => {
  // Reload project graph when toggle changes
  if (selectedProjectUuid.value) {
    loadProjectGraph()
  }
})

watch(selectedProjectConceptUuid, (newValue) => {
  if (newValue && selectedProjectUuid.value) {
    // Set to concept view and reload graph when concept is selected
    viewMode.value = 'concept'
    loadProjectGraph()
  } else if (!newValue && selectedProjectUuid.value) {
    // Clear concept view, reload project view
    viewMode.value = 'project'
    loadProjectGraph()
  }
})

watch(viewMode, () => {
  // Reload graph when view mode changes
  if (selectedProjectUuid.value) {
    loadProjectGraph()
  }
})

// Lifecycle
onMounted(async () => {
  storeApp.setPageHeader({ header: 'Entity Relations Visualization' })

  // Load projects if not already loaded
  if (storeProject.projectList.length === 0) {
    const { fetchData } = readProjects()
    await fetchData()
  }
})

definePage({
  meta: {
    label: 'Relations',
    icon: IconMdiGraphOutline,
  },
})
</script>

<template>
  <div class="flex flex-col h-full bg-background">
    <!-- Header Section -->
    <div class="bg-background border-b border-muted px-6 py-4 shadow-sm">
      <div class="flex items-center justify-between gap-4">
        <div class="flex-1">
          <h1 class="text-2xl font-semibold text-foreground mb-1">
            Entity Relations Visualization
          </h1>
          <p class="text-sm text-muted-foreground">
            Explore relationships between projects, concepts, sections, departments, and assets
          </p>
        </div>
      </div>

      <!-- Selection Controls -->
      <div class="grid grid-cols-3 gap-4 mt-4">
        <!-- Column 1: Project Dropdown + Include Concepts Toggle -->
        <div class="space-y-2">
          <div>
            <label class="block text-sm font-medium text-foreground mb-1">
              Select Project
            </label>
            <TuringSelect
              v-model="selectedProjectUuid"
              :options="projectOptions"
              option-label="label"
              option-value="value"
              placeholder="Choose a project..."
              class="w-full"
              :disabled="loading"
              filter
            >
              <template #option="slotProps">
                <div class="flex items-center justify-between w-full gap-2">
                  <span>{{ slotProps.option.label }}</span>
                  <span
                    class="text-xs px-2 py-0.5 rounded"
                    :class="
                      slotProps.option.status === 'published'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-muted text-muted-foreground'
                    "
                  >
                    {{ slotProps.option.statusLabel }}
                  </span>
                </div>
              </template>
            </TuringSelect>
          </div>

          <!-- Include Concepts Toggle -->
          <div
            class="flex items-center gap-2 h-8"
            :class="!selectedProjectUuid ? 'invisible' : ''"
          >
            <Switch
              v-model="includeConcepts"
              :disabled="loading || viewMode === 'concept'"
            />
            <label class="text-sm text-foreground">
              Include concepts
            </label>
          </div>
        </div>

        <!-- Column 2: Concept Dropdown + View Mode Toggle -->
        <div class="space-y-2">
          <div>
            <label class="block text-sm font-medium text-foreground mb-1">
              View Concept Details
            </label>
            <TuringSelect
              v-model="selectedProjectConceptUuid"
              :options="currentProjectConceptOptions"
              option-label="label"
              option-value="value"
              placeholder="Choose a concept to view full tree..."
              class="w-full"
              :disabled="loading || !selectedProjectUuid || currentProjectConceptOptions.length === 0"
              filter
            />
          </div>

          <!-- View Mode Toggle -->
          <div
            class="flex items-center gap-2 h-8"
            :class="!selectedProjectConceptUuid ? 'invisible' : ''"
          >
            <label class="text-sm font-medium text-muted-foreground">
              View Mode:
            </label>
            <div class="flex rounded-lg border border-muted overflow-hidden">
              <button
                :class="[
                  'px-4 py-2 text-sm font-medium transition-colors',
                  viewMode === 'project'
                    ? 'bg-primary text-white'
                    : 'bg-background text-foreground hover:bg-accent'
                ]"
                :disabled="!selectedProjectConceptUuid"
                @click="viewMode = 'project'"
              >
                Project
              </button>
              <button
                :class="[
                  'px-4 py-2 text-sm font-medium transition-colors border-l border-muted',
                  viewMode === 'concept'
                    ? 'bg-primary text-white'
                    : 'bg-background text-foreground hover:bg-accent'
                ]"
                :disabled="!selectedProjectConceptUuid"
                @click="viewMode = 'concept'"
              >
                Concept
              </button>
            </div>
          </div>
        </div>

        <!-- Column 3: Clear Button + Loading Indicator -->
        <div class="flex items-start justify-end gap-3 pt-6">
          <Button
            label="Clear"
            severity="secondary"
            outlined
            :icon="IconMdiCloseCircleOutline"
            :disabled="!selectedProjectUuid"
            @click="clearSelection"
          />

          <!-- Loading Indicator -->
          <div
            v-if="loading"
            class="flex items-center"
          >
            <Loader size="medium" />
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="error"
        class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-2"
      >
        <div class="flex items-start gap-2">
          <Icon :icon="IconMdiAlertCircle" severity="destructive" class="text-xl mt-0.5 flex-shrink-0" />
          <div>
            <p class="text-sm font-medium text-red-800">
              Error loading data
            </p>
            <p class="text-sm text-red-700 mt-1">
              {{ error }}
            </p>
          </div>
        </div>
        <Button
          label="Retry"
          size="small"
          severity="danger"
          outlined
          :icon="IconMdiRefresh"
          @click="retryLoadProject()"
        />
      </div>

      <!-- Node Limit Warning -->
      <div
        v-if="showNodeLimitWarning"
        class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start justify-between gap-2"
      >
        <div class="flex items-start gap-2">
          <Icon :icon="IconMdiAlert" severity="warning" class="text-xl mt-0.5 flex-shrink-0" />
          <div>
            <p class="text-sm font-medium text-yellow-800">
              Large Graph Warning
            </p>
            <p class="text-sm text-yellow-700 mt-1">
              This graph contains {{ nodeCount }} nodes, which may impact performance. Consider zooming and panning carefully.
            </p>
          </div>
        </div>
        <IconButton
          :icon="IconMdiClose"
          size="icon-sm"
          severity="warning"
          @click="dismissNodeLimitWarning"
        />
      </div>
    </div>

    <!-- Main Graph Container -->
    <div class="flex-1 relative border border-background">
      <!-- Empty State -->
      <div
        v-if="!selectedProjectUuid && !loading"
        class="absolute inset-0 flex items-center justify-center"
      >
        <div class="text-center max-w-md px-4">
          <Icon :icon="IconMdiGraphOutline" class="text-6xl mb-4" />
          <h3 class="text-lg font-semibold text-foreground mb-2">
            No Project Selected
          </h3>
          <p class="text-sm text-muted-foreground">
            Select a project from the dropdown above to visualize its relationships and dependencies.
          </p>
          <div class="mt-6 p-4 bg-accent border border-muted rounded-lg text-left">
            <h4 class="text-sm font-semibold text-foreground mb-2">
              Tips:
            </h4>
            <ul class="text-sm text-muted-foreground space-y-1">
              <li>• Projects show setup departments and their assets</li>
              <li>• Concepts show sections, departments, assets, and tank types</li>
              <li>• Click on any node to view detailed information</li>
              <li>• Use mouse wheel to zoom and drag to pan</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Graph Visualization -->
      <div
        v-else-if="graphNodes.length > 0"
        class="h-full relative"
      >
        <CytoscapeGraph
          :nodes="graphNodes"
          :edges="graphEdges"
          @node-click="handleNodeClick"
        />

        <!-- Graph Legend -->
        <GraphLegend />

        <!-- Loading Overlay (when re-rendering or updating) -->
        <div
          v-if="loading"
          class="absolute inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-30 pointer-events-none"
        >
          <div class="bg-background border border-muted rounded-lg shadow-lg p-6 flex flex-col items-center gap-3">
            <Loader size="large" />
            <p class="text-sm text-foreground font-medium">
              Building graph...
            </p>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-else-if="loading"
        class="absolute inset-0 flex items-center justify-center bg-muted"
      >
        <div class="text-center">
          <Loader size="large" />
          <p class="mt-4 text-sm text-muted-foreground">
            Loading relationship data...
          </p>
        </div>
      </div>

      <!-- No Data State -->
      <div
        v-else-if="!loading && selectedProjectUuid"
        class="absolute inset-0 flex items-center justify-center"
      >
        <div class="text-center max-w-md px-4">
          <Icon :icon="IconMdiDatabaseOffOutline" class="text-6xl mb-4" />
          <h3 class="text-lg font-semibold text-foreground mb-2">
            No Data Available
          </h3>
          <p class="text-sm text-muted-foreground">
            The selected entity has no related data to visualize.
          </p>
        </div>
      </div>
    </div>

    <!-- Entity Detail Panel -->
    <EntityDetailPanel
      :selected-node="selectedNode"
      :open="detailPanelOpen"
      @close="handleDetailPanelClose"
    />
  </div>
</template>

<style scoped>
/* Additional styles if needed */
</style>
